import { z } from "zod";
import { COLLECTION_STATUS } from "./collections.js";
// 🔴 US-E24.F63-0754 — DÙNG LẠI enum trạng thái lịch, không khai bản thứ hai (§3.8).
import { scheduleStatusSchema } from "./collection-schedules.js";

/* ===========================================================================
 * US-E24.F2-0351 — VÒNG ĐỜI ĐỢT THU: tạo (đúng MỘT lần) → sống → đóng/huỷ
 * (@UC9564 @UC9565 @UC9566 @UC9567 — @gate money).
 *
 * Repo trước task này chỉ có "tạo". Không đường huỷ, không đường đóng ⇒ một đợt thu tạo nhầm để lại
 * `member_dues` `pending` treo VĨNH VIỄN cho cả nhóm, và cách "sửa" duy nhất là bảo mọi người bỏ qua
 * đợt thu ma. Đó chính là thứ phá niềm tin mà Goppy tồn tại để bảo vệ.
 *
 * 🔴 QĐ PO 06/08/2026 (index §7) — hai đường TÁCH BẠCH, không nhập một:
 *  - **Huỷ** (`DELETE`): CHỈ khi CHƯA AI ĐÓNG. Đợt + mọi due chưa đóng biến mất trong CÙNG 1 tx.
 *    Có dù chỉ một người đã chạm tiền ⇒ **409 fail-closed** + hướng sang "đóng đợt".
 *  - **Đóng** (`PATCH .../close`): `status='closed'`. Dues giữ NGUYÊN bit-for-bit. Đây là đường
 *    thoát ĐÚNG cho thủ quỹ tạo nhầm một đợt đã có người đóng.
 *
 * 🔴 0 BÚT TOÁN ở CẢ HAI đường. Huỷ ≠ hoàn tiền: tiền đã vào quỹ là FACT ngoài đời — đảo bút toán
 * không làm tiền quay về ví ai, chỉ làm sổ lệch thực tế (§3.2/§3.3; QĐ PO Q1).
 * ========================================================================= */

/**
 * 409 — tạo đợt thu TRÙNG NỘI DUNG (@UC9564, S01). `code` ổn định, ngôn-ngữ-trung-tính; client map
 * `code → message` và gợi ý người dùng đổi tiêu đề nếu thật sự muốn hai đợt riêng.
 */
export const COLLECTION_DUPLICATE_CREATE_CODE = "COLLECTION_DUPLICATE_CREATE" as const;

/**
 * 422 — `memberIds` có người **thuộc nhóm nhưng KHÔNG `active`** (@UC9713, US-E24.F33-0447).
 * Client map → *"Có người trong danh sách đã rời nhóm — bỏ họ ra rồi tạo lại."*
 *
 * 🔴 TÁCH KHỎI 404 CÓ CHỦ ĐÍCH. 404 dùng cho id **không thuộc nhóm** (mirror `FundAccessGuard`:
 * không phân biệt "không tồn tại" với "không thuộc nhóm bạn" ⇒ không làm oracle dò id). Còn "đã rời
 * nhóm" là tình huống **có thật, sửa được**, và caller đã là OWNER/ADMIN/TREASURER — họ đọc được
 * danh sách thành viên rồi, nên vế này KHÔNG lộ thêm gì. Gộp cả hai vào 404 chỉ khiến thủ quỹ đi tìm
 * một thành viên "không tồn tại" mà họ vừa nhìn thấy trên màn hình.
 *
 * ⚠️ Đường LỊCH ĐỊNH KỲ (@UC9616) **cố ý KHÔNG có** mã này: phạm vi lịch neo `groupMemberId` chứ
 * không neo `status`, vì còn *kỳ sau* để người tạm nghỉ quay lại. Đợt MỘT LẦN không có kỳ sau — due
 * sinh ngay lúc bấm — nên chọn người đã rời nhóm là đòi tiền một người không còn ở đó.
 */
export const COLLECTION_MEMBER_NOT_ACTIVE_CODE = "COLLECTION_MEMBER_NOT_ACTIVE" as const;

/**
 * 422 — NHÓM CHƯA CÓ SUẤT NÀO ĐỂ THU (US-E24.F41-0733, QĐ người dùng 08/09/2026).
 *
 * Client map → *"Nhóm chưa có thành viên nào để thu. Hãy thêm thành viên trước khi tạo đợt thu."*
 * (nhóm phụ huynh: thêm học sinh, và chọn mình làm người đại diện nếu bạn là phụ huynh).
 *
 * 🔴 VÌ SAO PHẢI CÓ MÃ RIÊNG, không để im lặng như trước: từ 0733, ghế người lớn không còn là thành
 * viên, nên một nhóm phụ huynh **vừa lập** có diện thu RỖNG. Đường sinh due gặp tập rỗng thì
 * `enrollMembers` trả `0` và không ném gì — thủ quỹ bấm "Tạo đợt thu", thấy đợt thu hiện ra, và tin
 * là cả lớp đã được thu. Một đợt thu 0 nghĩa vụ trông y hệt một đợt thu thành công.
 *
 * ⚠️ KHÔNG dùng lại [COLLECTION_MEMBER_NOT_ACTIVE_CODE]: mã đó nói *"người bạn CHỌN không hợp lệ"*
 * và client đang map thành câu chỉ đúng tên một người. Ở đây người dùng chưa chọn ai cả — bịa nghĩa
 * mới cho một mã ổn định là làm client hiển thị sai câu (§7: `code` là mã ổn định, ngôn-ngữ-trung-tính).
 */
export const COLLECTION_NO_ELIGIBLE_MEMBER_CODE = "COLLECTION_NO_ELIGIBLE_MEMBER" as const;

/**
 * 409 — không huỷ được vì ĐÃ CÓ NGƯỜI CHẠM TIỀN (@UC9565/@UC9566, S02).
 * Client map → "Đợt này đã có người đóng nên không xoá được. Bạn có thể **đóng đợt** để dừng thu."
 */
export const COLLECTION_HAS_PAID_DUES_CODE = "COLLECTION_HAS_PAID_DUES" as const;

/**
 * 409 — đợt thu thuộc một VÒNG ĐỜI KHÁC, endpoint này không được phép đụng vào (REVIEW C1/H3).
 *
 * 🔴 Vì sao fail-closed theo `kind` chứ không "cho chạy hết cho tiện":
 *
 * **`settlement`** — bất biến *"1 settlement OPEN / quỹ"* (`settlement-collection.service.ts`) được
 * cài bằng `findFirst({kind:'settlement', status:'open'})`. Trước task này nó **tuyệt đối**, vì
 * `closed` không có luồng nghiệp vụ nào đặt được. Nếu `close()` đóng được một settlement đang mở thì
 * lần tất toán sau **không thấy đợt cũ nữa ⇒ tạo đợt thứ hai**, mà `computeSettlementOwed`
 * (`financial-position.service.ts`) cộng **MỌI** due chưa đóng của quỹ **không lọc `kind`** ⇒ due mới
 * = nợ gốc + due tất toán cũ ⇒ **đòi 2× rồi 3×, cộng dồn mỗi vòng**. Và kịch bản kích hoạt lại đúng
 * là thứ ta khuyên người dùng làm ("đã có người đóng ⇒ hãy ĐÓNG đợt").
 *
 * **`periodic`** — chỉ chặn ở `cancel()`. Đợt định kỳ do `collection_schedules` sinh ra và có vòng
 * đời riêng (pause/resume/cancel/reprice/skip). Xoá hàng `collection` chỉ xoá luôn khoá
 * `one_collection_per_schedule_period` ⇒ lần cron kế tiếp **sinh lại y hệt** ⇒ "đã huỷ" là một lời
 * nói dối có hạn dùng vài giờ. Muốn dừng thu định kỳ thì dừng LỊCH, không xoá một kỳ.
 * `close()` vẫn cho phép với `periodic` — đó chính là consumer mà S03 hồi sinh
 * (`prorata-due.producer.ts` chặn tính lại pro-rata trên kỳ đã đóng sổ).
 *
 * §2 index.md đã viết sẵn nguyên tắc này cho `collection_schedules`: *"đó là cơ chế khác, đừng nhập
 * hai vòng đời làm một"*. C1 là đúng cái giá của việc quên áp nó cho `settlement`.
 */
export const COLLECTION_NOT_CANCELABLE_KIND_CODE = "COLLECTION_NOT_CANCELABLE_KIND" as const;

/** 409 — `PATCH .../close` trên đợt thuộc vòng đời khác (hiện tại: `settlement`). Xem code trên. */
export const COLLECTION_NOT_CLOSABLE_KIND_CODE = "COLLECTION_NOT_CLOSABLE_KIND" as const;

/* ─────────────────── US-E24.F63-0754 — DỪNG CẢ QUỸ THU, KHÔNG PHẢI MỘT KỲ ───────────────────
 *
 * 🔴 BUG NGƯỜI DÙNG 09/09/2026: nút "Ngừng hoạt động" gọi `PATCH .../close` — đóng sổ đúng MỘT kỳ.
 * `CollectionSchedule.scheduleStatus` không đổi, mà worker sinh kỳ mới theo CHÍNH cờ đó
 * (`recurring-collection.job`: `where scheduleStatus='active'`) ⇒ tuần sau quỹ sống lại và tiếp tục
 * đòi tiền. Người dùng nói đúng: *"ngừng hoạt động là ngừng cả quỹ thu, chứ không phải ngừng mỗi kỳ"*.
 *
 * Ba mã dưới đây là ba câu TỪ CHỐI khác nhau — cố ý không gộp, vì client phải nói ba câu khác nhau
 * cho người dùng, và "một mã cho nhiều lý do" là thứ buộc client đoán.
 */

/** 409 — `stop-schedule` trên đợt LẺ: nó không thuộc lịch nào để mà dừng. Dùng `close` cho đợt lẻ. */
export const COLLECTION_NOT_PERIODIC_CODE = "COLLECTION_NOT_PERIODIC" as const;

/**
 * 409 — `reopen` một KỲ trong khi CẢ QUỸ đang dừng (QĐ người dùng 09/09/2026).
 *
 * Quỹ dừng thì mọi kỳ của nó dừng theo; kích hoạt lẻ một kỳ sẽ tạo ra một cái quỹ nửa sống nửa
 * chết — thu được tiền kỳ này nhưng không kỳ nào sinh tiếp. Muốn thu lại thì bật cả quỹ.
 */
export const SCHEDULE_PAUSED_CODE = "SCHEDULE_PAUSED" as const;

/**
 * 409 — `reopen` một kỳ mà hôm nay đã RA NGOÀI khoảng kỳ đó (QĐ người dùng 09/09/2026).
 *
 * *"HK1 (1/9 – 1/1), dừng 3/9, mở lại 5/9 thì được; đứng ở 2/2 mở lại thì báo lỗi."* Mở một kỳ đã
 * trôi qua là bịa ra một nghĩa vụ thu cho quãng thời gian đã khép — muốn truy thu thì tạo đợt lẻ,
 * có tiêu đề và có lý do (cùng lẽ với việc worker KHÔNG sinh bù kỳ đã lỡ).
 *
 * 🔴 Đo bằng `periodEnd` (hết KHOẢNG KỲ), KHÔNG bằng `dueDate`: quá hạn đóng mà kỳ chưa hết thì
 * vẫn phải cho thu nốt người trễ — đó là ca thường, không phải ngoại lệ.
 */
export const PERIOD_ALREADY_PASSED_CODE = "PERIOD_ALREADY_PASSED" as const;

/**
 * Kết quả `POST .../collections/:collectionId/stop-schedule` — DỪNG CẢ QUỸ, tính từ kỳ đang xem.
 *
 * Hai thực thể đổi trong MỘT transaction nên response nói cả hai: lịch chuyển `paused` (không sinh
 * kỳ mới nữa) và chính kỳ này đóng sổ (QĐ người dùng: *"ngừng là ngừng ngay"*).
 *
 * 🔴 CỐ Ý KHÔNG đóng sổ các kỳ CŨ còn mở: người còn thiếu ở kỳ trước vẫn phải nộp được. Đóng chúng
 * là cắt đường trả một khoản nợ CÓ THẬT — §3.6 *"máy giữ sổ, người giữ tình: máy không tự xoá nợ"*.
 */
export const stopCollectionScheduleResponseSchema = z
  .object({
    collectionId: z.string().uuid(),
    scheduleId: z.string().uuid(),
    /** Trạng thái CỦA KỲ sau thao tác — luôn `closed`. */
    collectionStatus: z.literal(COLLECTION_STATUS.CLOSED),
    /** Trạng thái CỦA LỊCH sau thao tác — `paused`, hoặc `cancelled` nếu nó vốn đã bị huỷ hẳn. */
    scheduleStatus: scheduleStatusSchema,
    /** `true` khi lịch VỐN đã dừng từ trước ⇒ thao tác chỉ chốt nốt kỳ này. Idempotent, không 409. */
    alreadyStopped: z.boolean(),
  })
  .strict();

export type StopCollectionScheduleResponse = z.infer<typeof stopCollectionScheduleResponseSchema>;

/** Trạng thái vòng đời hợp lệ mà `PATCH .../close` được phép đặt. */
export const closeCollectionRequestSchema = z
  .object({
    status: z.literal(COLLECTION_STATUS.CLOSED),
  })
  .strict();

export type CloseCollectionRequest = z.infer<typeof closeCollectionRequestSchema>;

/**
 * `PATCH .../reopen` — MỞ LẠI đợt đã đóng sổ (US-E24.F59-0524 · @UC9566 · @gate money 🔴).
 *
 * ⚠️ Docblock của `closeCollectionRequestSchema` trước đây ghi *"mở lại đợt KHÔNG thuộc task này"*.
 * Câu đó đúng cho `US-E24.F2-0351`; **QĐ người dùng 23/08/2026 (0524) đưa nó vào phạm vi** vì thiết
 * kế có cặp nút "Ngừng hoạt động" ↔ "Kích hoạt", và một nút Ngừng bấm nhầm mà không có đường lùi
 * thì buộc người dùng tạo đợt mới — tức đẻ một đợt trùng trong sổ để sửa một cú bấm nhầm.
 *
 * 🔴 ĐIỀU PHẢI BIẾT TRƯỚC KHI ĐỤNG VÀO: mở lại đợt **NỚI một guard tiền**.
 * `packages/domain/src/prorata-due.producer.ts:123` từ chối ghi `member_dues` khi đợt `closed`
 * (docblock `:29` gọi là *"KHÔNG HỒI TỐ — append-only tinh thần"*). Đưa đợt về `open` nghĩa là
 * đường ghi đó sống lại cho ĐÚNG đợt này.
 *
 * Ba chốt đổi lấy việc nới đó, **không được bỏ chốt nào**:
 *  1. Chỉ người có quyền (thừa hưởng `FundAccessGuard` + `@RequireFundRoles`) — như `close`.
 *  2. `@Audited` ghi `collection.reopen` ⇒ luôn trả lời được "ai mở lại, lúc nào".
 *  3. Client PHẢI cảnh báo tường minh trước khi gửi (thẻ xác nhận), không phải một nút lặng lẽ.
 *
 * `settlement` **không** mở lại được — nó cũng chưa từng đóng được (xem
 * `COLLECTION_NOT_CLOSABLE_KIND_CODE`), nên "mở lại" không có nghĩa; trả cùng mã 409 để một luật
 * chỉ có một mã.
 */
export const reopenCollectionRequestSchema = z
  .object({
    status: z.literal(COLLECTION_STATUS.OPEN),
  })
  .strict();

export type ReopenCollectionRequest = z.infer<typeof reopenCollectionRequestSchema>;

/** Trần độ dài tiêu đề khi SỬA — dùng lại đúng trần của đường TẠO (`adhocCollection`). */
export const COLLECTION_TITLE_MAX_LENGTH = 120;

/** Trần độ dài mô tả khi SỬA — dùng lại đúng trần của đường TẠO. */
export const COLLECTION_EDIT_DESCRIPTION_MAX = 500;

/**
 * `PATCH .../collections/:collectionId` — sửa **TÊN + MÔ TẢ** của đợt thu (US-E24.F59-0524 · @UC9565).
 *
 * 🔴 KHÔNG có trường tiền, và đó là RANH GIỚI của endpoint này, không phải thiếu sót:
 * thiết kế (`532:21926`) chỉ có hai ô Tên và Mô tả. Sửa số tiền của một đợt đã phát sinh
 * `member_dues` là một bài toán khác hẳn — nó phải quyết định số phận của những người đã đóng theo
 * mức cũ. Ai định thêm `amountMinor` vào đây: **DỪNG, hỏi người** (`CLAUDE.md` §6).
 *
 * `.strict()` ⇒ gửi kèm field lạ (kể cả `amountMinor`) là **400 ở biên, 0 row chạm DB**.
 *
 * Cả hai trường đều **optional** nhưng phải có **ít nhất một** — một PATCH rỗng là một lời gọi ghi
 * không mang ý định nào, và nó vẫn tốn một dòng audit khiến nhật ký khó đọc về sau.
 */
export const updateCollectionInfoRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(COLLECTION_TITLE_MAX_LENGTH).optional(),
    // `''` hợp lệ và mang nghĩa "xoá mô tả" — khác `undefined` ("không đụng tới").
    // Phân biệt được hai chuyện đó là điều kiện để người dùng gỡ được một mô tả đã gõ nhầm.
    description: z.string().trim().max(COLLECTION_EDIT_DESCRIPTION_MAX).optional(),
  })
  .strict()
  .refine((v) => v.title !== undefined || v.description !== undefined, {
    message: "Phải sửa ít nhất một trong hai: title hoặc description",
  });

export type UpdateCollectionInfoRequest = z.infer<typeof updateCollectionInfoRequestSchema>;

/**
 * Kết quả sửa thông tin đợt thu.
 *
 * 🔴 Trả `scheduleUpdated` để client nói ĐÚNG SỰ THẬT với người dùng: với đợt **định kỳ**, sửa tên/mô
 * tả áp cho **cả lịch** (kỳ tương lai) lẫn kỳ đang mở. Không trả cờ này thì màn hình chỉ có thể nói
 * chung chung "đã lưu", trong khi người dùng vừa đổi một thứ ảnh hưởng tới các tháng sau.
 */
export const updateCollectionInfoResponseSchema = z
  .object({
    collectionId: z.string().uuid(),
    title: z.string(),
    description: z.string().nullable(),
    /** `true` khi đợt thuộc một `collection_schedule` và lịch đó đã được cập nhật cùng lúc. */
    scheduleUpdated: z.boolean(),
  })
  .strict();

export type UpdateCollectionInfoResponse = z.infer<typeof updateCollectionInfoResponseSchema>;

/**
 * Kết quả một thao tác vòng đời.
 *  - `collectionId` — đợt bị tác động.
 *  - `outcome` — `cancelled` (đợt bị xoá hẳn) | `closed` (đợt còn, chuyển `closed`) |
 *    `already_closed` (đóng lại một đợt đã đóng — vô hại, idempotent).
 *  - `duesRemoved` — số due bị xoá cùng đợt (luôn `0` ở nhánh `closed`/`already_closed`).
 *  - `collectionStatus` — trạng thái ĐỢT sau thao tác; `null` khi đợt đã bị xoá.
 *
 * 🔴 KHÔNG trả bất kỳ số tiền nào: endpoint này không đụng tiền, và một con số tiền trong response
 * của một thao tác không-tiền chỉ tạo ảo giác rằng nó có.
 */
export const COLLECTION_LIFECYCLE_OUTCOME = {
  CANCELLED: "cancelled",
  CLOSED: "closed",
  ALREADY_CLOSED: "already_closed",
  // US-E24.F59-0524 — cặp đối xứng của `closed`/`already_closed`. Giữ `already_*` cho cả hai chiều
  // vì cùng một lý lẽ: bấm lại một thao tác đã ở đúng trạng thái là vô hại, 409 chỉ tạo bế tắc mới.
  REOPENED: "reopened",
  ALREADY_OPEN: "already_open",
} as const;

export type CollectionLifecycleOutcome =
  (typeof COLLECTION_LIFECYCLE_OUTCOME)[keyof typeof COLLECTION_LIFECYCLE_OUTCOME];

export const collectionLifecycleResponseSchema = z
  .object({
    collectionId: z.string().uuid(),
    outcome: z.enum([
      COLLECTION_LIFECYCLE_OUTCOME.CANCELLED,
      COLLECTION_LIFECYCLE_OUTCOME.CLOSED,
      COLLECTION_LIFECYCLE_OUTCOME.ALREADY_CLOSED,
      COLLECTION_LIFECYCLE_OUTCOME.REOPENED,
      COLLECTION_LIFECYCLE_OUTCOME.ALREADY_OPEN,
    ]),
    duesRemoved: z.number().int().min(0),
    collectionStatus: z
      .enum([COLLECTION_STATUS.OPEN, COLLECTION_STATUS.CLOSED])
      .nullable(),
  })
  .strict();

export type CollectionLifecycleResponse = z.infer<typeof collectionLifecycleResponseSchema>;

/** Outbox event khi một đợt thu bị HUỶ — nhóm đã được báo "có đợt thu mới", phải được báo nó không còn. */
export const COLLECTION_CANCELLED_EVENT = "collection.cancelled" as const;
